import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { API_BASE, setAuthToken } from '@/lib/api';
import { UserPlus, ShieldCheck, KeyRound } from 'lucide-react';

// Aug 2026 doctrine — Member Seat invitation-claim flow.
// The invitation link takes the seat member here. We fetch the seat's
// server-resolved owner+email context, let them set a PIN, and sign them
// in on submit. The owner's account id is never exposed to the URL or UI.

const AcceptInvite = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';
  const [state, setState] = useState({ loading: true, error: null, info: null });
  const [pin, setPin] = useState('');
  const [pinConfirm, setPinConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setState({ loading: false, error: 'Missing invitation token.', info: null });
      return;
    }
    (async () => {
      try {
        const r = await axios.get(`${API_BASE}/team/invitation/${encodeURIComponent(token)}`);
        setState({ loading: false, error: null, info: r.data });
      } catch (e) {
        const msg = e?.response?.data?.detail || 'This invitation is invalid or expired.';
        setState({ loading: false, error: msg, info: null });
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!/^\d{4,8}$/.test(pin)) {
      toast.error('PIN must be 4–8 digits.');
      return;
    }
    if (pin !== pinConfirm) {
      toast.error('Both PIN entries must match.');
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API_BASE}/team/invitation/claim`, {
        token,
        pin,
      });
      setAuthToken(r.data.token);
      toast.success('PIN set — signed in.');
      navigate('/', { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not claim invitation.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-orange-50 flex items-center justify-center p-4" data-testid="accept-invite-page">
      <div className="w-full max-w-md">
        {state.loading && (
          <div className="rounded-2xl bg-white border border-slate-200 p-6 text-center text-slate-600" data-testid="invite-loading">
            Checking your invitation…
          </div>
        )}

        {state.error && (
          <div className="rounded-2xl bg-white border-2 border-red-200 p-6 text-center" data-testid="invite-error">
            <div className="mx-auto rounded-full bg-red-50 p-3 w-12 h-12 flex items-center justify-center mb-3">
              <ShieldCheck className="text-red-600" size={22} />
            </div>
            <h1 className="text-lg font-bold text-slate-900 mb-2">Invitation unavailable</h1>
            <p className="text-sm text-slate-600">{state.error}</p>
            <p className="text-xs text-slate-500 mt-4">
              Ask your account owner to resend the invitation from their Team Seats page.
            </p>
          </div>
        )}

        {state.info && !state.error && (
          <div className="rounded-2xl bg-white border border-slate-200 shadow-lg p-6 space-y-4" data-testid="invite-claim-form">
            <div className="text-center">
              <div className="mx-auto rounded-full bg-orange-50 p-3 w-14 h-14 flex items-center justify-center mb-3">
                <UserPlus className="text-orange-600" size={26} />
              </div>
              <h1 className="text-xl font-bold text-slate-900">
                Join {state.info.owner_display_name} on Zita
              </h1>
              <p className="text-sm text-slate-600 mt-1">
                Signing in as <b>{state.info.email}</b>
              </p>
            </div>

            <form onSubmit={submit} className="space-y-3">
              <label className="block">
                <span className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
                  Create your PIN (4–8 digits)
                </span>
                <div className="relative mt-1">
                  <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="\d{4,8}"
                    value={pin}
                    onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 8))}
                    placeholder="••••"
                    autoFocus
                    className="w-full pl-9 pr-3 py-2.5 rounded-lg border-2 border-slate-200 focus:border-orange-400 outline-none text-lg tabular-nums tracking-widest"
                    data-testid="invite-pin"
                  />
                </div>
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
                  Confirm PIN
                </span>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="\d{4,8}"
                  value={pinConfirm}
                  onChange={(e) => setPinConfirm(e.target.value.replace(/\D/g, '').slice(0, 8))}
                  placeholder="••••"
                  className="w-full mt-1 px-3 py-2.5 rounded-lg border-2 border-slate-200 focus:border-orange-400 outline-none text-lg tabular-nums tracking-widest"
                  data-testid="invite-pin-confirm"
                />
              </label>
              <button
                type="submit"
                disabled={submitting}
                className="w-full py-2.5 rounded-lg bg-slate-900 text-white font-semibold hover:bg-slate-700 disabled:opacity-50"
                data-testid="invite-submit"
              >
                {submitting ? 'Setting up…' : 'Set up my Zita access'}
              </button>
            </form>
            <p className="text-xs text-slate-500 text-center">
              Invitation expires {String(state.info.expires_at).slice(0, 10)}. Keep your PIN private — you'll use email + PIN to sign in going forward.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AcceptInvite;
